"use client"

import { Head, router } from "@inertiajs/react"
import {
  Archive,
  ArrowLeft,
  CheckDone01,
  Edit01,
  Eye,
  EyeOff,
  MessageChatSquare,
  PieChart01,
  Target01,
  Upload01,
  Users01,
} from "@untitledui/icons"
import type { CSSProperties } from "react"
import { useState } from "react"

import { ContributorsSection } from "@/components/admin/collabs/contributors-section"
import { PromptDisplay } from "@/components/admin/collabs/prompt-display"
import { CollabStatusBadge } from "@/components/admin/collabs/status-badge"
import { SuggestionCard } from "@/components/admin/collabs/suggestion-card"
import { FaqAccordion } from "@/components/collabs/faq-accordion"
import { StatCard } from "@/components/dashboard/stat-card"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { Link } from "@/components/ui/link"
import { RichText } from "@/components/ui/rich-text"
import { Select } from "@/components/ui/select"
import { TabList, Tabs } from "@/components/ui/tabs"
import { WarningStackedLeftAlignedModal } from "@/components/ui/warning-stacked-left-aligned-modal"
import { DashboardLayout } from "@/layouts/dashboard-layout"
import { tagLabel } from "@/lib/tags"
import { focalPointStyle } from "@/lib/utils"
import {
  adminCollabSuggestionsPath,
  adminCollabsPath,
  archiveAdminCollabPath,
  collabPath,
  editAdminCollabPath,
  publishAdminCollabPath,
} from "@/routes"
import type { BriefFaq, FocalPoint } from "@/types"
import type {
  BriefContributor,
  BriefProgress,
  EditorialStrategySummary,
  TrackPromptSummary,
} from "@/types/contents"
import { BriefStatus } from "@/types/contents"
import type { CollabSuggestion } from "@/types/contents/collab_suggestion"
import type { Brief } from "@/types/serializers"
import type { Tagging } from "@/types/tag"

/*
 * Maps collab status variable to status object for utility.
 * The status object provides clarity of availability attributes (e.g next allowed status, action url, label).
 */
interface CollapStatusOption {
  label: string
  value: BriefStatus
  actionPath?: (slug: string) => string
  next: CollapStatusOption[]
}

type CollapStatusType = Record<BriefStatus, CollapStatusOption[]>

const collabStatuses: CollapStatusType = {
  [BriefStatus.draft]: [
    {
      label: "Draft",
      value: BriefStatus.draft,
      get next() {
        return [
          collabStatuses[BriefStatus.archived],
          collabStatuses[BriefStatus.published],
        ].flat()
      },
    },
  ],
  [BriefStatus.published]: [
    {
      label: "Publish",
      value: BriefStatus.published,
      actionPath: (slug) => publishAdminCollabPath(slug),
      get next() {
        return collabStatuses[BriefStatus.archived]
      },
    },
  ],
  [BriefStatus.archived]: [
    {
      label: "Archive",
      value: BriefStatus.archived,
      actionPath: (slug) => archiveAdminCollabPath(slug),
      get next() {
        return collabStatuses[BriefStatus.published]
      },
    },
  ],
}

// `track_prompts[].track_suggestions` is a plain (non-traited) Alba
// attribute on TrackPromptResource, so it always ships — Typelizer just
// can't read the block, same `unknown` gap as the moderation queue's
// TrackSuggestionResource fields (see suggestion-card.tsx).
interface CollabShowTrackPrompt extends TrackPromptSummary {
  track_suggestions: CollabSuggestion[]
}

/*
 * This is the narrowed re-declarations of fields on `Brief["brief"]`
 * For this page renders as nested objects rather than the serializer's raw/loose typing.
 */
interface CollabShowBrief
  extends Omit<
    Brief["brief"],
    | "progress"
    | "featured_contributors"
    | "track_contributors"
    | "track_prompts"
    | "editorial_strategy"
    | "taggings"
    | "cover_image_url"
    | "cover_image_focal_point"
    | "contributors_count"
  > {
  status: BriefStatus
  progress: BriefProgress
  featured_contributors: BriefContributor[]
  track_contributors: BriefContributor[]
  track_prompts: CollabShowTrackPrompt[]
  editorial_strategy: EditorialStrategySummary | null
  taggings: Tagging[]
  cover_image_url: string | null
  cover_image_focal_point: FocalPoint | null
  contributors_count: number
}

const SUGGESTIONS_PREVIEW_LIMIT = 3
interface CollabsShowProps {
  brief: CollabShowBrief
}

/**
 * CollabsShow is a page component for showing a collab & prompt info.
 */
export default function CollabsShow({ brief }: CollabsShowProps) {
  const [pending, setPending] = useState(false)
  const [suggestionsExpanded, setSuggestionsExpanded] = useState(false)
  const [isOpenConfirmModal, setIsOpenConfirmModal] = useState(false)
  const [nextDraftedStatus, setNextDraftedStatus] =
    useState<BriefStatus | null>(null)

  /*
   * Performs the actual publish/archive PATCH and tracks pending
   * state while it reloads. `brief.status` is the Inertia prop,
   * already fresh after the redirect (accepted or refused), so
   * there's no local status to mirror it into.
   */
  const submitStatusChange = (status: BriefStatus) => {
    const url = collabStatuses[status][0]?.actionPath?.(brief.slug)
    if (!url) return

    setPending(true)
    router.patch(
      url,
      {},
      {
        preserveScroll: true,
        onFinish: () => setPending(false),
      },
    )
  }
  /*
   * Trigger Clears the cached status selection whenever the confirm modal closes,
   * whatever triggered the close (confirm, cancel, backdrop dismiss, escape).
   */
  const handleInterectConfirmModal = (open: boolean) => {
    if (!open) {
      setNextDraftedStatus(null)
    }
    setIsOpenConfirmModal(open)
  }

  /*
   * Fires a confirm modal when the Collab's status selection is changed.
   * This function is only a UI hanldler for showing a confirm modal and caching chagned status in ref.
   * The submission logic held in the onConfirm() handler of the WarningStackedLeftAlignedModal component.
   */
  const handleOpenConfirmModal = (status: BriefStatus) => {
    if (brief.status === BriefStatus.draft) {
      setNextDraftedStatus(status)
      setIsOpenConfirmModal(true)
      return
    }
  }

  /*
   * Fires confirmed modal to perform drafted status changes and clear cache from ref value.
   * This function is essentially helping for next status swapping between published & archived.
   */
  const confirmDraftedStatusChange = () => {
    if (nextDraftedStatus && brief.status === BriefStatus.draft) {
      submitStatusChange(nextDraftedStatus)
    }
    setNextDraftedStatus(null)
    setIsOpenConfirmModal(false)
  }

  /*
   * Swaps the action path between archived and published before submit.
   */
  const swapActionPathBeforeSubmit = (status: BriefStatus) => {
    switch (status) {
      case BriefStatus.archived:
        submitStatusChange(BriefStatus.published)
        break
      case BriefStatus.published:
        submitStatusChange(BriefStatus.archived)
        break
      default:
        return
    }
  }

  const { progress } = brief

  const allSuggestions = brief.track_prompts
    .flatMap((prompt) => prompt.track_suggestions)
    .sort((a, b) => b.created_at.localeCompare(a.created_at))
  const visibleSuggestions =
    suggestionsExpanded || allSuggestions.length < SUGGESTIONS_PREVIEW_LIMIT
      ? allSuggestions
      : allSuggestions.slice(0, SUGGESTIONS_PREVIEW_LIMIT)
  const clippedSuggestionsCount =
    allSuggestions.length - SUGGESTIONS_PREVIEW_LIMIT

  return (
    <DashboardLayout>
      <Head title={brief.title} />
      <main className="mx-auto flex w-full max-w-5xl flex-col gap-6 px-4 pt-8 pb-12 lg:px-8">
        <Link
          href={adminCollabsPath()}
          className="text-tertiary hover:text-secondary flex items-center gap-1.5 text-sm font-medium"
        >
          <ArrowLeft aria-hidden="true" className="size-4" />
          All collabs
        </Link>

        <header className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
          <div className="flex flex-col gap-4 sm:flex-row sm:items-start">
            <div className="flex flex-col gap-1">
              <div className="flex flex-wrap items-center gap-2">
                <h1 className="text-primary font-display md:text-display-xs text-xl font-semibold">
                  {brief.title}
                </h1>
                <CollabStatusBadge status={brief.status} />
              </div>
              {brief.editorial_strategy && (
                <p className="text-quaternary text-sm">
                  Editorial strategy:{" "}
                  <span className="text-tertiary">
                    {brief.editorial_strategy.title}
                  </span>
                </p>
              )}
            </div>
          </div>
          <div className="flex items-center gap-3">
            {brief.status === BriefStatus.published ? (
              <Button
                color="secondary"
                size="md"
                href={collabPath(brief.slug)}
                target="_blank"
                iconLeading={Eye}
              >
                View
              </Button>
            ) : (
              <Button color="secondary" size="md" iconLeading={EyeOff} disabled>
                View
              </Button>
            )}
            <Button
              color="secondary"
              size="md"
              href={editAdminCollabPath(brief.slug)}
              iconLeading={Edit01}
            >
              Edit
            </Button>
            {collabStatuses[brief.status][0].value === BriefStatus.draft &&
            collabStatuses[brief.status][0].next.length > 1 ? (
              <Select
                key={nextDraftedStatus}
                aria-label="Change drafted collab status"
                onChange={(key) => {
                  handleOpenConfirmModal(key as BriefStatus)
                }}
                placeholder="Drafted"
              >
                {collabStatuses[BriefStatus.draft][0].next.map((status) => (
                  <Select.Item key={status.value} id={status.value}>
                    {status.label}
                  </Select.Item>
                ))}
              </Select>
            ) : (
              <Button
                color={
                  collabStatuses[brief.status][0].next[0].value ===
                  BriefStatus.published
                    ? "primary"
                    : "secondary"
                }
                iconLeading={
                  collabStatuses[brief.status][0].next[0].value ===
                  BriefStatus.published
                    ? Upload01
                    : Archive
                }
                size="md"
                onClick={() => swapActionPathBeforeSubmit(brief.status)}
                className="min-w-20"
                isDisabled={pending}
              >
                {collabStatuses[brief.status][0].next[0].label}
              </Button>
            )}
          </div>
        </header>

        <section
          aria-label="Detail"
          className="flex flex-col justify-between gap-4 sm:flex-row"
        >
          <div className="flex max-w-xl min-w-0 flex-1 flex-col gap-2">
            {/* Body contents  */}
            {brief.subtitle && (
              <p className="text-md text-tertiary">{brief.subtitle}</p>
            )}
            {brief.main_headline && (
              <h2 className="text-primary text-lg font-semibold">
                {brief.main_headline}
              </h2>
            )}
            {brief.strategic_context && (
              <p className="text-md text-secondary leading-relaxed">
                {brief.strategic_context}
              </p>
            )}
            {brief.main_body && (
              <RichText
                content={brief.main_body}
                className="[&_p]:leading-relaxed"
              />
            )}
            {brief.summary && (
              <p className="text-md text-tertiary">{brief.summary}</p>
            )}
            {/* Tags */}
            {brief.taggings.length > 0 && (
              <div className="mt-2 flex max-w-2xl flex-wrap gap-2">
                {brief.taggings.map((tagging) => (
                  <Badge
                    key={tagging.id}
                    size="sm"
                    color={tagging.is_visible ? "brand" : "gray"}
                    type="pill-color"
                  >
                    {tagLabel(tagging.tag)}
                  </Badge>
                ))}
              </div>
            )}
          </div>

          {/* Cover image */}
          <CoverImage
            src={brief.cover_image_url}
            alt={brief.slug}
            style={focalPointStyle(brief.cover_image_focal_point)}
          />
        </section>

        {/* Stat cards */}
        <section aria-label="Progress" className="flex flex-col gap-3">
          {progress.almost_there && (
            <Badge
              color="warning"
              size="sm"
              type="pill-color"
              className="self-start"
            >
              Almost there
            </Badge>
          )}
          <div className="grid grid-cols-2 gap-4 sm:grid-cols-3">
            <StatCard
              icon={CheckDone01}
              color="success"
              label="Prompts filled"
              value={progress.filled_slots_count}
            />
            <StatCard
              icon={Target01}
              label="Total Prompts"
              value={progress.total_slots}
            />
            <StatCard
              icon={PieChart01}
              color="warning"
              label="Fill rate (%)"
              value={progress.percent}
            />
            <StatCard
              icon={MessageChatSquare}
              label="Total suggestions"
              value={progress.total_suggestions_count}
            />
            <StatCard
              icon={MessageChatSquare}
              label="Suggestions"
              value={brief.suggestions_count}
            />
            <StatCard
              icon={Users01}
              label="Total Contributors"
              value={brief.contributors_count}
            />
          </div>
        </section>

        <ContributorsSection
          title="Featured contributors"
          contributors={brief.featured_contributors}
        />
        <ContributorsSection
          title="Track contributors"
          contributors={brief.track_contributors}
        />

        {/* Tab menu - prompt, community suggestion */}
        <section aria-label="prompt " className="mt-4 flex flex-col">
          <h2 className="text-primary text-sm font-semibold">
            Prompts & Community Suggestions
          </h2>
          <Tabs
            orientation="horizontal"
            defaultSelectedKey="prompts"
            className="mt-4"
          >
            <TabList
              size="sm"
              type="underline"
              items={[
                {
                  id: "prompts",
                  label: "Prompts",
                  badge: brief.track_prompts.length || undefined,
                },
                {
                  id: "suggestions",
                  label: "Suggestions",
                  badge: progress.total_suggestions_count || undefined,
                },
              ]}
            />
            <Tabs.Panel
              id="prompts"
              className="bg-surface-grouped mt-2 rounded-xl p-2"
            >
              <PromptDisplay
                collabSlug={brief.slug}
                trackPrompts={brief.track_prompts}
              />
            </Tabs.Panel>
            <Tabs.Panel
              id="suggestions"
              className="bg-surface-grouped mt-2 flex flex-col gap-4 rounded-xl p-6"
            >
              {progress.total_suggestions_count > 0 ? (
                <div className="flex flex-col gap-3">
                  <div className="flex justify-end">
                    <Button
                      color="secondary"
                      size="sm"
                      href={adminCollabSuggestionsPath(brief.slug)}
                      iconLeading={MessageChatSquare}
                    >
                      Review suggestions
                    </Button>
                  </div>

                  {visibleSuggestions.length > 0 && (
                    <div className="item-center flex flex-col gap-3">
                      {visibleSuggestions.map((suggestion) => (
                        <SuggestionCard
                          key={suggestion.id}
                          suggestion={suggestion}
                        />
                      ))}
                    </div>
                  )}

                  {allSuggestions.length >= SUGGESTIONS_PREVIEW_LIMIT && (
                    <div className="flex justify-center">
                      <Button
                        type="button"
                        size="sm"
                        color="link-color"
                        onClick={() => setSuggestionsExpanded((prev) => !prev)}
                      >
                        {suggestionsExpanded
                          ? "See less"
                          : `See more (${clippedSuggestionsCount})`}
                      </Button>
                    </div>
                  )}
                </div>
              ) : (
                <div className="flex items-center justify-center">
                  <p className="text-tertiary text-sm">
                    No suggestions yet — they&apos;ll show up here as visitors
                    and guests submit tracks.
                  </p>
                </div>
              )}
            </Tabs.Panel>
          </Tabs>
        </section>

        {/* FAQ */}
        <section aria-label="FAQ" className="flex flex-col">
          <span aria-hidden className="scotch-rule mb-10" />
          <FaqAccordion faqs={brief.brief_faqs as BriefFaq[]} />
        </section>
      </main>

      <WarningStackedLeftAlignedModal
        isOpen={isOpenConfirmModal}
        onOpenChange={handleInterectConfirmModal}
        title={
          nextDraftedStatus
            ? `Change status to ${nextDraftedStatus}?`
            : "Change draft status?"
        }
        description={
          nextDraftedStatus
            ? `This can't be undone. The collab won't be able to go back to draft once it's ${nextDraftedStatus}.`
            : "This can't be undone. The collab won't be able to go back to draft."
        }
        onConfirm={confirmDraftedStatusChange}
        confirmLabel="Confirm"
      />
    </DashboardLayout>
  )
}

/**
 * CoverImage - a fragment component for showing image with fallback handling.
 */
function CoverImage({
  src,
  alt,
  style,
}: {
  src?: string | null
  alt?: string
  style?: CSSProperties
}) {
  const image = src ? (
    <img src={src} alt={alt} className="size-full object-cover" style={style} />
  ) : (
    <div className="flex size-full items-center justify-center">
      <svg
        className="text-fg-quaternary size-10"
        fill="none"
        viewBox="0 0 24 24"
        stroke="currentColor"
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={1.5}
          d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"
        />
      </svg>
    </div>
  )
  return (
    <div className="bg-secondary relative aspect-[3/2] w-full shrink-0 overflow-hidden rounded-xl sm:aspect-auto sm:w-64 sm:self-stretch">
      {image}
    </div>
  )
}
